import { orderBy } from 'lodash';
import { t } from 'i18next';
import i18n from './helpers/i18n';

const __baseObjectType = {
  __orderedKey: undefined,
  __getValue(v: number | null | undefined) {
    const keys = Object.keys(this);
    return keys.find(k => this[k] === v) || '';
  },
  __getKeys() {
    return Object.keys(this).filter(x => !x.startsWith('__'));
  },
  // Libellés traduits (étape 5) : clé « enum.<Valeur> », sinon la valeur elle-même (noms propres : Bixolon, Monekto…).
  __getSelectOptions() {
    return (this.__orderedKey || orderBy(this.__getKeys(), x => x)).map(k => {
      return { label: enumLabel(k), value: this[k] };
    });
  },
};
export const enumLabel = (key: string) => i18n.t(`enum.${key}`, { defaultValue: key });
export const AuditLogType = {
  Create: 1,
  Update: 2,
  Delete: 3,
  ...__baseObjectType,
};
export const UserType = {
  GlobalUser: 1,
  CompanyUser: 2,
  ...__baseObjectType,
};

export const PartnerAccountType = {
  BankAccount: 1,
  MobileMoneyAccount: 2,
  ...__baseObjectType,
};
// Type d'un partenaire de paiement (serveur : PaymentPartnerType). Différent de PartnerAccountType (comptes des
// partenaires « utility ») : ne pas les confondre.
export const PaymentPartnerKind = {
  MNO: 1,
  Bank: 2,
  ...__baseObjectType,
};
export const EncryptionType = {
  None: 1,
  TripleDes: 2,
  RSA: 3,
  BlowFish: 4,
  TwoFish: 5,
  AES: 6,
  ...__baseObjectType,
};

export const MarriedStatusType = {
  Married: 1,
  UnMarried: 2,
  Divorced: 3,
  Widow: 4,
  ...__baseObjectType,
};
// Sexe affiché traduit partout (Homme, Femme) ; clés GenderType_Male / GenderType_Female.
export const GenderType = {
  ...__baseObjectType,
  Male: 1,
  Female: 2,
  __getValue(v: number | string | null | undefined) {
    const key = typeof v === 'string' ? v : Object.keys(this).find(k => this[k] === v);
    return key && !key.startsWith('__') && this[key] !== undefined ? i18n.t('GenderType_' + key) : '';
  },
  __getSelectOptions() {
    return this.__getKeys().map(k => ({ label: i18n.t('GenderType_' + k), value: this[k] }));
  },
};
export const SystemAppType = {
  Monekto: 1,
  ConektoRetail: 2,
  ConektoTransport: 3,
  ...__baseObjectType,
};

export const MasterServiceType = {
  ...__baseObjectType,
  OverTheCounter: 1,
  CashIn: 2,
  CashOut: 3,
  OneaBillPayment: 4,
  SonabelBillPayment: 5,
  CarParking: 6,
  MotorbikeParking: 7,
  Shipments: 8,
  OverTheCounterTicketBooking: 9,
  SubscriptionPlanPayment: 10,
  LianeTicketBooking: 11,
  LianeRestaurantOrder: 12,
};
export const ServiceFeePayingEntityType = {
  ...__baseObjectType,
  Merchant: 1,
  TransportCompanies: 2,
  MonektoUsers: 3,
  Customer: 4,
  __orderedKey: ['Customer', 'Merchant', 'TransportCompanies', 'MonektoUsers'],
};

export const ServiceFeeSharingEntityType = {
  ...__baseObjectType,
  Mtopo: 1,
  Operators: 2,
  Merchants: 3,
  Companies: 4,
  __orderedKey: ['Mtopo', 'Operators', 'Merchants', 'Companies'],
};

export const GUID = {
  EMPTY: '00000000-0000-0000-0000-000000000000',
};
export const SoftwareComponentType = {
  ...__baseObjectType,
  Basic: 1,
  Optional: 2,
};
export const PaymentTermsType = {
  ...__baseObjectType,
  PrePaid: 1,
  PostPaid: 2,
};

export const PaymentModeType = {
  ...__baseObjectType,
  Cash: 1,
  MobileMoney: 2,
  CreditCard: 3,
  Miles: 4,
  Credit: 5,
  Wallet: 6,
  // P43 : part d'une vente payée par un avoir.
  CreditNote: 7,
};
export const PlanType = {
  ...__baseObjectType,
  First: 1,
  Default: 2,
  Paid: 3,
};
export const SmsProviderType = {
  ...__baseObjectType,
  CompanySmsService: 1,
  Twilio: 2,
};
export const BlobStorageContainerType = {
  CompaniesContainer: 1,
  EmployeesContainer: 2,
  GaragesContainer: 3,
  StationsContainer: 4,
  VendorsContainer: 5,
  WarehousesContainer: 6,
  ShipmentsContainer: 7,
  ParkingSellContainer: 8,
  HardwareContainer: 9,
  BusContainer: 10,
  ExpenseContainer: 11,
  PartnersContainer: 12,
  CaisseContainer: 13,
  TransportPassengers: 14,
  // Privés : lus seulement par l'API (justificatifs de solde, journaux d'import), jamais par URL publique.
  StationBalanceProofs: 15,
  ImportLogs: 16,
};

export const ActionCasseType = {
  ...__baseObjectType,
  Oui: 1,
  Non: 0,
};
export const ShipmentInsuranceSettingType = {
  ...__baseObjectType,
  FlatBase: 1,
  PercentageBase: 2,
  RangeBase: 3,
  __getValue(v: number | null | undefined) {
    const keys = Object.keys(this);
    let key = keys.find(k => this[k] === v) || '';
    return i18n.t('ShipmentInsuranceSettingType_' + key);
  },
  __getSelectOptions() {
    return (this.__orderedKey || orderBy(this.__getKeys(), x => x)).map(k => {
      return { label: i18n.t('ShipmentInsuranceSettingType_' + k), value: this[k] };
    });
  },
};
export const CompanyAccountType = {
  ...__baseObjectType,
  Payment: 1,
  CashInCashOut: 2,
};

export const NotificationContactType = {
  ...__baseObjectType,
  SMS: 1,
  Email: 2,
};
/** Type de passager d'un tarif du catalogue global (P4) : règle d'accompagnement, rapports. Clés de traduction. */
export const PassengerTypeValue = { Adult: 1, Infant: 2, Child: 3 };
export const PassengerTypeLabel: Record<number, string> = { 1: 'passengerType_Adult', 2: 'passengerType_Infant', 3: 'passengerType_Child' };
export const passengerTypeOptions = (t: (k: string) => string) =>
  [PassengerTypeValue.Adult, PassengerTypeValue.Child, PassengerTypeValue.Infant].map(v => ({ value: v, label: t(PassengerTypeLabel[v]) }));

export const TariffCategoryType = {
  ...__baseObjectType,
  Person: 1,
  Age: 2,
  // Profession avait la même valeur que PhysicalConditions (4) : 3 depuis le lot P4.
  Profession: 3,
  PhysicalConditions: 4,
  Special: 5,
  TicketConditions: 6,
  CarteLoyalty: 7,
  Partner: 8,
  Regularisation: 9,
};
export const EmployeeType = {
  ...__baseObjectType,

  Employee: 1,
  ExternalUser: 2,
};
export const TransportRoleType = {
  ...__baseObjectType,
  AccountAdministrator: 'Account Administrator',
  AccountOwner: 'Account Owner',
  Accounting: 'Accounting',
  BaggageHandler: 'Baggage Handler',
  Cashier: 'Cashier',
  CashierColis: 'Cashier Colis',
  CheckInOperator: 'CheckIn Operator',
  CoDriver: 'Co-Driver',
  Driver: 'Driver',
  Facturier: 'Facturier',
  FleetSupervisor: 'Fleet Supervisor',
  GarageSupervisor: 'Garage Supervisor',
  Inventory: 'Inventory',
  Manifeste: 'Manifeste',
  ParkingHandler: 'Parking Handler',
  Procurement: 'Procurement',
  SalesSupervisor: 'Sales Supervisor',
  StationAdministrator: 'Station Administrator',
  StationSupervisor: 'Station Supervisor',
  Treasurer: 'Treasurer',
  WarehouseSupervisor: 'Warehouse Supervisor',
  GlobalCashier: 'Global Cashier',
};

export const StationBalanceType = {
  ...__baseObjectType,

  Internal: 1,
  External: 2,
  __getSelectOptions() {
    return [{ get label() { return t('External'); }, value: 2 }];
  },
};

export const PrinterCompanyType = {
  ...__baseObjectType,
  EPSON_LAN: 1,
  BIXOLON_USB: 2,
};
export const PrintType = {
  ...__baseObjectType,
  Receipts: 1,
  Tags: 2,
};
export const AccountActionType = {
  ...__baseObjectType,
  Deposit: 1,
  WithDraw: 2,
  PartnerBooking: 3,
  __getSelectOptions() {
    return [{ get label() { return t('WithDraw'); }, value: 2 }];
  },
};
export const AccounEventType = {
  ...__baseObjectType,
  Created: 1,
  Used: 2,
  Transferred: 3,
  Recharged: 4,
  StationTransfer: 5,
  Updated: 6,
  Payment: 7,
};
export const StationBalanceEntityRef = {
  ...__baseObjectType,

  Register: 1,
  StationCreated: 2,
  CreditSellPayment: 4,
  RegisterWithDraw: 5,
  RegisterDeposit: 6,
  StationInitialized: 7,
  ExpensePayment: 8,
  StationTransfer: 9,
  RegisterReconciliation: 10,
  CaisseTransfered: 11,
  CaisseReconciliationTransfer: 12,
  ExternalTransfer: 13,
  ShipmentReimbursementExpense: 14,
};

export const SeatingType = {
  ...__baseObjectType,
  '2X2 (5)': 1,
  '3X2 (6)': 2,
  '3X3 (7)': 3,
  '2X3 (6)': 4,
};

export const BusEntityType = {
  ...__baseObjectType,
  Seat: 1,
  ToiletLeft: 2,
  ToiletRight: 3,
  Fridge: 4,
  Door: 5,
};

export const PassengerType = {
  ...__baseObjectType,

  Adult: 1,
  Children: 2,
  Infant: 3,
};

export const PassengerTitleType = {
  ...__baseObjectType,

  Mr: 'Mr',
  Mrs: 'Mrs',
  Ms: 'Ms',
  Master: 'Master',
  Miss: 'Miss',
};

export const RouteType = {
  ...__baseObjectType,

  International: 1,
  National: 2,
};

export const SegmentTariffParameterValueType = {
  ...__baseObjectType,
  FixedValue: 1,
  Percentage: 2,
};

export const SegmentTariffParameterDirectionType = {
  ...__baseObjectType,
  Increase: 1,
  Decrease: 2,
};

export const DayOfWeekType = {
  ...__baseObjectType,
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,

  __orderedKey: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

export const DiscountValueType = {
  ...__baseObjectType,

  FixedValue: 1,
  PercentageBase: 2,
};

export const DiscountBeneficiaryType = {
  ...__baseObjectType,

  Passenger: 1,

  TicketClass: 3,
  TariffParameter: 4,
};

export const DiscountTargetType = {
  ...__baseObjectType,

  Shipment: 1,
  Baggage: 2,
  Trip: 3,
};

export const TripStatusType = {
  ...__baseObjectType,

  Scheduled: 1,
  CheckedIn: 2,
  Boarding: 3,
  Departed: 4,
  Left: 5,
  Arrived: 6,
  Cancelled: 7,
  Delayed: 8,
  Reached: 9,
  Interrupted: 10,
  Restarted: 11,
  __getColor: v => ['', '#1890ff', '#20c997', '#fa8b0c', '#04503a', '#a91666', '#a17506', '#e3091b', '#9e14cf', '#98a1a9', '#98a1a9'][v],
};

export const RegisterStatus = {
  Ready: 1,
  Open: 2,
  Closed: 3,
  ...__baseObjectType,
  __getColor: v => ['', '#20c997', '#fa8b0c', '#e3091b'][v],
  __getCreateRegisterSelectOptions() {
    return [
      { get label() { return t('Ready'); }, value: 1 },
      { get label() { return t('Closed'); }, value: 3 },
    ];
  },
};

export const PrintDestinationType = {
  Bixolon: 1,
  Extension: 2,
  ...__baseObjectType,
};

export const ReceiptPrinterType = {
  'Bixolon SRP 350III(USB)': 1,
  'Bixolon SRP S300(USB)': 2,
  'EPSON(USB)': 3,
  'TSC TE244(USB)': 4,
  ...__baseObjectType,
};

export const ShipmentLabelPrinterType = {
  'SLP - DX220 (USB)': 1,
  'SRP - 350III (USB)': 2,
  'SRP - S300 (USB)': 3,
  'Epson Tm-m30 (USB)': 4,
  'TSC TE244 (USB)': 5,
  ...__baseObjectType,
};
export const TicketPrinterType = {
  'Bixolon SRP 350III (USB)': 1,
  'Bixolon SRP S300 (USB)': 2,
  'EPSON (USB)': 3,
  'Bixolon SLP DX220 (USB) - New Logo': 4,
  'Bixolon SLP DX220 (USB) - New': 5,
  'TSC TE244 (USB) - New Logo': 6,
  'TSC TE244 (USB) - New': 7,
  ...__baseObjectType,
};

export const TicketStatusType = {
  ...__baseObjectType,
  Issued: 1,
  CheckedIn: 2,
  Cancelled: 3,
  Pending: 4,

  Expired: 5,
  UnUsed: 6,
  Boarded: 7,
  Waiting: 8,
  Free: 9,
  CancelRequested: 10,
  Replaced: 11,
  // Libellé traduit : clé « enum.<Valeur> », comme les listes de filtres.
  __getValue(v: number | null | undefined) {
    const key = this.__getKeys().find((k: string) => this[k] === v);
    return key ? enumLabel(key) : '';
  },
  __getColor: () => {
    return 'white';
  },
  __getBgColor: (v: any) => {
    return [undefined, '#919993', '#0fbf38', '#720600', '#f5aa42', '#bf081a', '#cdd1d3', '#0fbf38', '#919090', '#ed53c4', '#720600', '#6b7280'][v];
  },
};

export const RegisterBalanceType = {
  Deposit: 1,
  Withdraw: 2,
  ...__baseObjectType,
};

export const SupportedLanguageType = {
  English: 'en',
  French: 'fr',
  ...__baseObjectType,
};

export const PaymentMethodType = {
  ...__baseObjectType,
  Cash: 1,
  MobileMoney: 2,
};

export const PrinterType = {
  ...__baseObjectType,
  Receipts: 1,
  Tags: 2,
};

export const TripJourneyType = {
  OneWay: 0,
  Return: 1,
  ...__baseObjectType,
  __orderedKey: ['OneWay', 'Return'],

  __getLabel: (v: any) => {
    if (v === 0) {
      return enumLabel('OneWay');
    }
    if (v === 1) {
      return enumLabel('Return');
    }
    return '';
  },
};

export const SeatStatusType = {
  Available: 1,
  Blocked: 2,
  Selected: 3,
  FreeBooked: 4,
  Booked: 5,
  Partner: 6,
  Online: 7,
  __getColor: (v: any) => ['', '#fff', '#454545', '#118ABE', '#00FF47', '#ED3326', '#D9B741', '#D9929C'][v],
};
export const SeatStatusList = [
  { color: '#ED3326', get label() { return t('Booked'); } },
  { color: '#118ABE', get label() { return t('Selected'); } },
  { color: '#00FF47', get label() { return t('Free booked'); } },
  { color: 'white', get label() { return t('Available'); } },
  { color: '#454545', get label() { return t('Blocked'); } },
  { color: '#D9B741 ', get label() { return t('Partner'); } },
  { color: '#D9929C ', get label() { return t('Online'); } },
];

export const IdentityDocumentType = {
  NationalIdentityCard: 1,
  Passport: 2,
  ...__baseObjectType,
};

export const DiscountApplicationTarget = {
  PassengerMobile: 1,
  PassengerWeb: 2,
  ...__baseObjectType,
};

export const DiscountCompanyType = {
  AllCompanies: 1,
  CustomCompanies: 2,
  ...__baseObjectType,
};

export const DiscountUsageType = {
  OneTime: 1,
  MultipleTimes: 2,
  ...__baseObjectType,
};

export const DiscountUserType = {
  NewUsers: 1,
  OldUsers: 2,
  AllUsers: 3,
  CustomUsers: 4,
  ...__baseObjectType,
};

export const BookingPayModeType = {
  Cash: 1,
  MobileMoney: 2,
  CreditCard: 3,
  Miles: 4,
  Credit: 5,
  Wallet: 6,
  ...__baseObjectType,
};

export const BookingStatusType = {
  Booked: 1,
  Cancelled: 2,
  PaymentPending: 3,
  ...__baseObjectType,
};

export const ConfirmationBookingNotificationType = {
  None: 0,
  Sms: 1,
  Email: 2,
  SmsAndEmail: 3,

  ...__baseObjectType,
};

export const CashierReportType = {
  Day: 1,
  Year: 2,

  ...__baseObjectType,
  __getSelectOptions() {
    return [
      {
        get label() { return t('Year'); },
        value: 2,
      },
      { get label() { return t('Next 7 days'); }, value: 1 },
    ];
  },
};

export const AdvertisingScreenType = {
  BeforeLogin: 1,
  Home: 2,
  ...__baseObjectType,
};

export const AdvertisingType = {
  Slider: 1,
  StaticPanel: 2,
  ...__baseObjectType,
};

export const AdvertisingDetailType = {
  Image: 1,
  Video: 2,
  ...__baseObjectType,
};
export const VideoPlayerType = {
  Upload: 1,
  Youtube: 2,

  ...__baseObjectType,
};

export const AdvertisingClickActionType = {
  None: 1,
  LinkToTripDetail: 2,
  ExternalUrl: 3,

  ...__baseObjectType,
};

export const PassengerUserSource = {
  ...__baseObjectType,

  RegisterOnLiane: 1,
  Facebook: 2,
  Apple: 3,
};

export const UserFollowUpType = {
  ...__baseObjectType,
  DidNotAnswer: 1,
  AskedToCallBack: 2,
  Positive: 3,
  Negative: 4,
  Moderate: 5,
  NotUsingApp: 6,
};

export const BaggageType = {
  ...__baseObjectType,
  StandardBag: 1,
  ExtraBag: 2,
  BagMoto: 3,
};
